import { Modal, SpinningLoader } from "@risalabs_frontend_org/oasis-ui-kit";
import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { ModalId } from "../../../enums/modalId";
import { TableNames } from "../../../enums/tableNames";
import { closeModal } from "../../../redux/slice/modalSliceNew";
import { AppDispatch } from "../../../redux/store/store";
import CustomTable from "../../custom-table/custom-table";
import { onPushToSftp } from "./actionHandlers/onPushToSftp";
import { useSftpPushPendingRecords } from "./hooks/useSftpPushPending";
import { generateSftpPushPendingRecordsTableHeaders } from "./utils/sftpPushPendingRecordsTableData";
import { toggleCheckboxSelection } from "./utils/toggleCheckboxSelection";
import {
  areAllPageItemsSelected,
  toggleSelectAllOnPage,
} from "./utils/toggleSelectAllOnPage";

const SftpPushPendingRecords = () => {
  const {
    tableData,
    setTableData,
    isLoading,
    showInlineLoader,
    totalCount,
    setEndIndex,
  } = useSftpPushPendingRecords({ page: 1, page_size: 400 });
  const dispatch = useDispatch<AppDispatch>();
  const [isPushing, setIsPushing] = useState(false);
  const [currentStartIndex, setCurrentStartIndex] = useState(0);
  const [currentEndIndex, setCurrentEndIndex] = useState(9); // Default to itemsPerPage - 1

  // Handle select all for current page only
  const handleSelectAll = () => {
    toggleSelectAllOnPage(currentStartIndex, currentEndIndex, setTableData);
  };

  // Check if all items on current page are selected
  const isAllPageItemsSelected = useMemo(
    () =>
      areAllPageItemsSelected(tableData, currentStartIndex, currentEndIndex),
    [tableData, currentStartIndex, currentEndIndex],
  );

  const handleSave = async () => {
    setIsPushing(true);
    try {
      await onPushToSftp(tableData, dispatch, setTableData);
    } finally {
      setIsPushing(false);
    }
  };

  const handleClose = () => {
    setTableData([]);
    dispatch(closeModal());
  };

  const saveButtonText = isPushing ? "Pushing..." : "Push to SFTP";

  return (
    <Modal
      dialogId={ModalId.SFTP_PUSH_PENDING_RECORDS_MODAL}
      title="SFTP Push Pending Records"
      onClose={handleClose}
      onSave={isPushing ? () => {} : handleSave}
      onCancel={handleClose}
      saveButtonText={saveButtonText}
      cancelText="Close"
      disableSave={isPushing || isLoading || tableData.length === 0}
      heightPercentage={60}
    >
      <div className="h-full w-full overflow-y-auto">
        {isLoading || totalCount === undefined ? (
          <div className="flex h-full items-center justify-center gap-2">
            <SpinningLoader />
            <span>Loading...</span>
          </div>
        ) : (
          <CustomTable
            tableHeaders={generateSftpPushPendingRecordsTableHeaders()}
            tableData={tableData}
            tableName={TableNames.SFTP_PUSH_PENDING_RECORDS_TABLE}
            itemsPerPage={10}
            pagesPerView={4}
            hideSearchBar={true}
            count={totalCount}
            totalCount={totalCount}
            toggleCheckboxSelection={(id) =>
              toggleCheckboxSelection(id, setTableData)
            }
            hideSelectAllCheckbox={false}
            onSelectAll={handleSelectAll}
            isCheckboxSelected={isAllPageItemsSelected}
            startIndexOfTable={setCurrentStartIndex}
            endIndexOfTable={(endIdx) => {
              setCurrentEndIndex(endIdx);
              setEndIndex(endIdx);
            }}
            showInLineLoader={showInlineLoader}
          />
        )}
      </div>
    </Modal>
  );
};

export default SftpPushPendingRecords;
